// The failing behaviour score card's view of its job, and what trust-score/behaviour-score's answer means for it. Pure, so the
// unit tests import it.
import { formatDate } from '../../../../helpers/format.js';

type InputValues = Readonly<Record<string, unknown>> | null | undefined;

export interface FailingView {
  companyName: string;
  companyId: string | null;
  // When the run of failures began (the record's behaviourFailure.since), as a date
  since: string | null;
  failures: number | null;
  reason: string | null;
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/** The card's job inputs, as the trust-score/review-queue cron filled them; anything missing or unreadable reads as unknown. */
export function failingView(inputs: InputValues): FailingView {
  const since = text(inputs?.failingSince);
  const failures = Number(text(inputs?.failedAttempts));
  return {
    companyName: text(inputs?.subjectCompanyName) ?? 'This company',
    companyId: text(inputs?.subjectCompanyId),
    since: since && !Number.isNaN(new Date(since).getTime()) ? formatDate(since, 'longDate') : null,
    failures: Number.isInteger(failures) && failures > 0 ? failures : null,
    reason: text(inputs?.failingReason),
  };
}

// What trust-score/behaviour-score answered inside its HTTP 200 (callLambda's res)
export interface ScoreAnswer {
  code?: unknown;
  reason?: unknown;
  message?: unknown;
  outcome?: unknown;
}

/** What the card says after Score now: worked (and whether the score moved), refused, or failed again. */
export function scoreNowResult(answer: ScoreAnswer): { ok: boolean; message: string } {
  if (answer.code === 200 && answer.outcome === 'skipped') {
    return { ok: false, message: "Nothing was scored: the company's trust score record is missing or incomplete." };
  }
  // Suppressed or unpublished since the lambda's own check: it answered 200 but wrote only the clock
  if (answer.code === 200 && answer.outcome === 'suppressed') {
    return {
      ok: false,
      message:
        "Nothing was scored: the company's trust score is suppressed, so its behaviour is not scored until it is published.",
    };
  }
  if (answer.code === 200 && answer.outcome === 'waiting') {
    return {
      ok: false,
      message:
        "Nothing was scored: the company's trust score has never been published, so its behaviour is not scored yet.",
    };
  }
  if (answer.code === 200) {
    return {
      ok: true,
      message:
        answer.outcome === 'changed'
          ? 'Scored: the behaviour score is up to date and published. Mark this resolved once you know why it failed.'
          : 'Scored: the behaviour score works again and has not changed. Mark this resolved once you know why it failed.',
    };
  }
  if (answer.reason === 'SUPPRESSED') {
    return {
      ok: false,
      message: "The company's trust score is suppressed, so its behaviour is not scored until it is published.",
    };
  }
  if (answer.reason === 'NOT_PUBLISHED') {
    return {
      ok: false,
      message: "The company's trust score has never been published, so its behaviour is not scored yet.",
    };
  }
  const message = text(answer.message);
  return {
    ok: false,
    message: answer.code === 500 || !message ? 'It failed again. Check the lambda logs for the reason.' : message,
  };
}
